/**
 * Suppliers List Reducer
 * Handles suppliers list state, pagination, and filters
 */

import { SUPPLIERS_ACTION_TYPES } from '../../actions/suppliersActions';

const initialState = {
  allSuppliers: [], // Added: Stores ALL suppliers from server
  suppliers: [],    // Stores filtered/visible suppliers
  loading: false,
  error: null
};

const suppliersListReducer = (state = initialState, action) => {
  switch (action.type) {
    case SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };

    case SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_SUCCESS:
      return {
        ...state,
        loading: action.payload.keepLoading ? true : false,
        error: null,
        allSuppliers: action.payload.items, // Store Full List
        suppliers: action.payload.updateVisible === false ? state.suppliers : action.payload.items,
      };
    case SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        allSuppliers: [], // Reset on error
        suppliers: []
      };

    case SUPPLIERS_ACTION_TYPES.CREATE_SUPPLIER_SUCCESS:
      return {
        ...state,
        allSuppliers: [...(state.allSuppliers || []), action.payload], // Append to master list
        suppliers: [...state.suppliers, action.payload], // Append to visible (will be re-filtered anyway)
        totalCount: (state.totalCount || 0) + 1
      };

    case SUPPLIERS_ACTION_TYPES.UPDATE_SUPPLIER_SUCCESS:
      // The payload holds only the changed fields: merge them (the page reloads the list from the server anyway).
      const updatedSupplier = action.payload;
      return {
        ...state,
        allSuppliers: (state.allSuppliers || []).map(s => s.id === updatedSupplier.id ? { ...s, ...updatedSupplier } : s),
        suppliers: state.suppliers.map(s =>
          s.id === updatedSupplier.id ? { ...s, ...updatedSupplier } : s
        )
      };

    case SUPPLIERS_ACTION_TYPES.DELETE_SUPPLIER_SUCCESS:
      const deletedId = action.payload.supplierId;
      return {
        ...state,
        allSuppliers: (state.allSuppliers || []).filter(s => s.id !== deletedId),
        suppliers: state.suppliers.filter(s => s.id !== deletedId),
        totalCount: Math.max(0, (state.totalCount || 0) - 1)
      };

    case SUPPLIERS_ACTION_TYPES.FILTER_SUPPLIERS_BY_CATEGORY:
      return {
        ...state,
        loading: false,
        error: null,
        suppliers: action.payload.items,
        pageNumber: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: action.payload.totalPages,
        hasNextPage: action.payload.hasNextPage,
        hasPreviousPage: action.payload.hasPreviousPage,
        filters: {
          ...state.filters,
          category: action.payload.filterValue,
          isActive: true,
          settlementIds: Array.isArray(action.payload.settlementIds) ? action.payload.settlementIds : [],
          settlements: Array.isArray(action.payload.selectedSettlements) ? action.payload.selectedSettlements : []
        },
        searchTerm: action.payload.searchTerm ?? state.searchTerm
      };

    case SUPPLIERS_ACTION_TYPES.FILTER_SUPPLIERS_BY_STATUS:
      return {
        ...state,
        loading: false,
        error: null,
        suppliers: action.payload.items,
        pageNumber: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: action.payload.totalPages,
        hasNextPage: action.payload.hasNextPage,
        hasPreviousPage: action.payload.hasPreviousPage,
        filters: {
          ...state.filters,
          status: action.payload.filterValue,
          isActive: true,
          settlementIds: [],
          settlements: []
        },
        searchTerm: action.payload.searchTerm ?? state.searchTerm
      };

    case SUPPLIERS_ACTION_TYPES.CLEAR_SUPPLIERS_FILTERS:
      return {
        ...state,
        loading: false,
        error: null
      };

    case SUPPLIERS_ACTION_TYPES.INVALIDATE_SUPPLIERS_CACHE:
      return {
        ...state,
        allSuppliers: [] // Clear cache to force refetch next time
      };

    default:
      return state;
  }
};

export default suppliersListReducer;